<template>
  <div
    v-if="open"
    class="ob-backdrop"
    @click.self="handleClose"
  >
    <div class="ob-modal">
      <header class="ob-head">
        <div>
          <div class="ob-kicker">
            Personnalisation en 30 sec
          </div>
          <h3 class="ob-title">
            Apprendre à ta façon
          </h3>
        </div>
        <button
          v-if="allowSkip"
          type="button"
          class="ob-close"
          @click="handleClose"
        >
          ×
        </button>
      </header>

      <div class="ob-progress">
        <div class="ob-progress-bar">
          <div
            class="ob-progress-fill"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <span class="ob-progress-text">{{ stepIndex + 1 }} / {{ steps.length }}</span>
      </div>

      <div class="ob-step">
        <h4 class="ob-question">
          {{ currentStep.prompt }}
        </h4>
        <p
          v-if="currentStep.help"
          class="ob-help"
        >
          {{ currentStep.help }}
        </p>

        <div
          v-if="currentStep.kind === 'choice'"
          class="ob-options"
        >
          <button
            v-for="option in currentStep.options"
            :key="option.value"
            type="button"
            class="ob-option"
            :class="{ selected: answers[currentStep.key] === option.value }"
            @click="setAnswer(currentStep.key, option.value, option.label)"
          >
            <span class="ob-option-emoji">{{ option.emoji }}</span>
            <span>
              <span class="ob-option-label">{{ option.label }}</span>
              <span
                v-if="option.hint"
                class="ob-option-hint"
              >{{ option.hint }}</span>
            </span>
          </button>
        </div>

        <div
          v-else-if="currentStep.kind === 'scale'"
          class="ob-scale"
        >
          <button
            v-for="n in 5"
            :key="'s' + n"
            type="button"
            class="ob-scale-btn"
            :class="{ selected: Number(answers[currentStep.key]) === n }"
            @click="setAnswer(currentStep.key, String(n), currentStep.scaleLabels ? currentStep.scaleLabels[n - 1] : '')"
          >
            {{ n }}
          </button>
          <div class="ob-scale-legend">
            <span>{{ currentStep.scaleMinLabel || 'Pas confiant' }}</span>
            <span>{{ currentStep.scaleMaxLabel || 'Très confiant' }}</span>
          </div>
        </div>

        <div
          v-else-if="currentStep.kind === 'text'"
          class="ob-text"
        >
          <input
            v-model="textValue"
            type="text"
            class="ob-text-input"
            :placeholder="currentStep.placeholder || ''"
            @input="setAnswer(currentStep.key, textValue)"
          >
        </div>
      </div>

      <footer class="ob-footer">
        <button
          type="button"
          class="ob-btn ghost"
          :disabled="stepIndex === 0"
          @click="prev"
        >
          Précédent
        </button>
        <div class="ob-footer-actions">
          <button
            v-if="allowSkip && stepIndex === 0"
            type="button"
            class="ob-btn link"
            @click="handleClose"
          >
            Plus tard
          </button>
          <button
            v-if="stepIndex < steps.length - 1"
            type="button"
            class="ob-btn primary"
            :disabled="!canAdvance"
            @click="next"
          >
            Continuer
          </button>
          <button
            v-else
            type="button"
            class="ob-btn primary"
            :disabled="!canAdvance || submitting"
            @click="submit"
          >
            {{ submitting ? 'Construction…' : 'Lancer mon parcours' }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
const DEFAULT_STEPS = [
  {
    key: 'goalMode',
    prompt: 'Pourquoi tu fais ce cours ?',
    help: 'Ça nous aide à calibrer le rythme.',
    kind: 'choice',
    options: [
      {
        value: 'exam', emoji: '🎯', label: 'Préparer un examen', hint: 'Dense, efficace',
      },
      {
        value: 'catchup', emoji: '🤝', label: 'Rattraper des lacunes', hint: 'Plus guidé, plus d\'exemples',
      },
      {
        value: 'mastery', emoji: '🧠', label: 'Vraiment maîtriser', hint: 'Équilibré, complet',
      },
      {
        value: 'explore', emoji: '🔭', label: 'Découvrir le sujet', hint: 'Ouvert, curieux',
      },
    ],
  },
  {
    key: 'declaredLevel',
    prompt: 'Ton niveau actuel sur ce sujet ?',
    kind: 'choice',
    options: [
      { value: 'beginner', emoji: '🌱', label: 'Débutant' },
      { value: 'intermediate', emoji: '📘', label: 'Intermédiaire' },
      { value: 'advanced', emoji: '🏆', label: 'Avancé' },
    ],
  },
  {
    key: 'explanationStyle',
    prompt: 'Tu préfères apprendre comment ?',
    kind: 'choice',
    options: [
      {
        value: 'example_first', emoji: '🔎', label: 'Par l\'exemple', hint: 'Un cas concret avant la règle',
      },
      {
        value: 'formula_first', emoji: '📐', label: 'Par la formule', hint: 'La règle d\'abord, les exemples après',
      },
      {
        value: 'step_by_step', emoji: '🪜', label: 'Pas à pas', hint: 'Étape par étape, sans sauter',
      },
    ],
  },
  {
    key: 'pace',
    prompt: 'Le rythme qui te va ?',
    kind: 'choice',
    options: [
      { value: 'steady', emoji: '🚶', label: 'Tranquille' },
      { value: 'focused', emoji: '⚡', label: 'Concentré' },
      { value: 'intensive', emoji: '🚀', label: 'Intensif' },
    ],
  },
]

export default {
  name: 'JourneyOnboardingModal',
  props: {
    open: { type: Boolean, default: false },
    allowSkip: { type: Boolean, default: true },
    submitting: { type: Boolean, default: false },
    courseTitle: { type: String, default: '' },
  },
  data() {
    return {
      stepIndex: 0,
      answers: {},
      textValue: '',
      steps: DEFAULT_STEPS,
    }
  },
  computed: {
    currentStep() {
      return this.steps[this.stepIndex] || this.steps[0]
    },
    progressPercent() {
      return Math.round(((this.stepIndex + 1) / this.steps.length) * 100)
    },
    canAdvance() {
      const step = this.currentStep
      if (step.optional) return true
      const val = this.answers[step.key]
      if (typeof val === 'string') return val.trim().length > 0
      return val !== undefined && val !== null
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.stepIndex = 0
        this.textValue = ''
      }
    },
    stepIndex() {
      const step = this.currentStep
      if (step.kind === 'text') {
        this.textValue = this.answers[step.key] || ''
      }
    },
  },
  methods: {
    setAnswer(key, value, label = '') {
      this.$set(this.answers, key, value)
      if (label) {
        this.$set(this.answers, `${key}_label`, label)
      }
    },
    prev() {
      if (this.stepIndex > 0) this.stepIndex--
    },
    next() {
      if (this.stepIndex < this.steps.length - 1) this.stepIndex++
    },
    submit() {
      const payload = this.steps.map(step => ({
        key: step.key,
        label: this.answers[`${step.key}_label`] || step.prompt,
        value: this.answers[step.key] || '',
      }))
      this.$emit('submit', payload)
    },
    handleClose() {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.ob-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(30, 22, 68, 0.55);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(4px);
}
.ob-modal {
  background: #fff;
  border-radius: 18px;
  width: min(560px, 100%);
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(20, 10, 60, 0.35);
}
.ob-head {
  padding: 22px 26px 14px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.ob-kicker {
  color: #7a6ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.ob-title {
  margin: 4px 0 0;
  font-size: 22px;
  color: #26254a;
  font-weight: 800;
}
.ob-close {
  border: none;
  background: transparent;
  color: #8b87b3;
  font-size: 26px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
.ob-progress {
  padding: 0 26px 16px;
  display: flex;
  gap: 14px;
  align-items: center;
}
.ob-progress-bar {
  flex: 1;
  height: 5px;
  background: #ece9fb;
  border-radius: 10px;
  overflow: hidden;
}
.ob-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7a6ed8, #5f5bd6);
  transition: width 0.25s ease;
}
.ob-progress-text {
  font-size: 12px;
  color: #8b87b3;
  font-weight: 600;
}
.ob-step {
  padding: 8px 26px 16px;
  overflow-y: auto;
}
.ob-question {
  margin: 0 0 6px;
  font-size: 18px;
  color: #26254a;
  font-weight: 700;
}
.ob-help {
  margin: 0 0 14px;
  color: #6b6996;
  font-size: 13px;
}
.ob-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ob-option {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  background: #fafaff;
  border: 2px solid #eae6fb;
  padding: 12px 14px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}
.ob-option:hover {
  border-color: #c8c0f0;
}
.ob-option.selected {
  border-color: #5f5bd6;
  background: #f1efff;
}
.ob-option-emoji { font-size: 22px; }
.ob-option-label {
  display: block;
  color: #26254a;
  font-weight: 600;
}
.ob-option-hint {
  display: block;
  color: #6b6996;
  font-size: 12px;
  margin-top: 2px;
}
.ob-scale {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 10px;
}
.ob-scale-btn {
  border: 2px solid #eae6fb;
  background: #fafaff;
  padding: 14px 0;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 700;
  color: #26254a;
  font-size: 16px;
  transition: all 0.15s ease;
}
.ob-scale-btn:hover { border-color: #c8c0f0; }
.ob-scale-btn.selected {
  background: #5f5bd6;
  color: #fff;
  border-color: #5f5bd6;
}
.ob-scale-legend {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #8b87b3;
  margin-top: 4px;
}
.ob-text-input {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #eae6fb;
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease;
}
.ob-text-input:focus { border-color: #5f5bd6; }
.ob-footer {
  padding: 14px 26px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eceaf6;
  background: #fafaff;
}
.ob-footer-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.ob-btn {
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;
}
.ob-btn.primary {
  background: #5f5bd6;
  color: #fff;
}
.ob-btn.primary:hover { background: #4a46c0; }
.ob-btn.primary:disabled { background: #c3bfec; cursor: not-allowed; }
.ob-btn.ghost {
  background: transparent;
  color: #5f5bd6;
  border: 1px solid #d9d5f0;
}
.ob-btn.ghost:disabled { color: #c3bfec; cursor: not-allowed; }
.ob-btn.link {
  background: transparent;
  color: #8b87b3;
  padding: 10px 6px;
}
.ob-btn.link:hover { color: #5f5bd6; }
</style>
